import { ChevronRight } from 'lucide-preact'
import type { BrandRolesViewDto, KnowledgeBaseCategoryStatusDto } from '@/types/knowledgeBase'
import {
  COLOR_ROLES,
  dayOf,
  inWords,
  roleTarget,
} from '@/components/console/knowledgeBase/brandGuidelinesModel'
import { wordsAreDraft, type EntryState } from '@/components/console/knowledgeBase/knowledgeBaseModel'
import type { BrandGuidelinesLoad } from './brandGuidelines/useBrandGuidelines'
import {
  DraftMark,
  ENTRY_CHEVRON_CELL,
  ENTRY_DESCRIPTION,
  ENTRY_NAME,
  ENTRY_ROW_GRID,
  ENTRY_STATE_CELL,
  EmptyMark,
  EntryStateLabel,
  ReviewedMark,
  STATE_NOTE,
  WarningMark,
} from './entryStatus'

interface Props {
  status: KnowledgeBaseCategoryStatusDto
  state: EntryState
  /** Brand Guidelines' own page at the level the knowledge base is showing. */
  href: string
  brand: BrandGuidelinesLoad
  /** Whose brand a workspace or campaign shows: its own, or the one it takes from above (ORB-1027). */
  ownerNote: string | null
}

/**
 * Brand Guidelines' row (ORB-904). It opens the brand's own page rather than expanding, because that is where it changes.
 * ⚑ Colors and words are reviewed separately, so each gets its own line: the colors' confirmation is read from the entry,
 * because the summary says only whether they are Webster's unconfirmed draft, not who confirmed them or where they came
 * from.
 */
export function BrandGuidelinesRow({ status, state, href, brand, ownerNote }: Props) {
  const set = brand.kind === 'ready' ? brand.entry?.BrandTokens ?? null : undefined
  const provenance = set?.Provenance
  const confirmedNote = provenance?.Confirmed ? [provenance.ConfirmedByName, dayOf(provenance.ConfirmedAt)].filter(Boolean).join(' · ') : ''

  return (
    <a href={href} className={`group ${ENTRY_ROW_GRID}`}>
      <span className="block min-w-0">
        <span className="flex items-center gap-3">
          <span className={ENTRY_NAME}>{status.CategoryDisplayName}</span>
          {set !== undefined && <RoleSwatches roles={set?.Roles ?? null} />}
        </span>
        <span className={ENTRY_DESCRIPTION}>Colors, type and logos Webster builds with, and the words that go with them</span>
      </span>
      <span className={ENTRY_STATE_CELL}>
        {state === 'missing' || state === 'empty' ? <EntryStateLabel state={state} />
          : provenance?.Confirmed ? (
            <>
              <span className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
                <ReviewedMark className="w-4 h-4 flex-none" />Colors confirmed
              </span>
              {confirmedNote && <span className={STATE_NOTE}>{confirmedNote}</span>}
            </>
          ) : provenance?.Source === 'Website' ? (
            <span className="flex items-center gap-2 text-sm text-purple-700 dark:text-purple-300">
              <DraftMark className="w-4 h-4 flex-none" />Colors: Webster draft
            </span>
          ) : provenance ? (
            <span className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
              <EmptyMark className="w-4 h-4 flex-none text-slate-400 dark:text-slate-500" />Colors not confirmed
            </span>
          ) : brand.kind === 'failed' ? (
            <span className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
              <WarningMark className="w-4 h-4 flex-none" />Colors could not load
            </span>
          ) : null}
        {wordsAreDraft(status) && (
          <span className="block mt-1.5 first:mt-0"><EntryStateLabel state="draft" draftLabel="Words: Webster draft" /></span>
        )}
        {ownerNote && <span className={STATE_NOTE}>{ownerNote}</span>}
      </span>
      <ChevronRight aria-hidden="true" className={`${ENTRY_CHEVRON_CELL} text-slate-500 dark:text-slate-400 group-hover:text-slate-700 dark:group-hover:text-slate-200`} />
    </a>
  )
}

/**
 * The five role colors, small, in the brand's own hexes. A role with no color is a dashed frame, so a missing Background
 * or Text shows here as well as in words.
 */
export function RoleSwatches({ roles, owner }: { roles: BrandRolesViewDto | null; owner?: string }) {
  const named = COLOR_ROLES.map(({ role }) => {
    const hex = roles?.[role].Hex ?? null
    return { role, hex, label: `${role} ${roleTarget(roles, role) || hex}` }
  })
  const missing = named.filter(swatch => swatch.hex === null).map(swatch => swatch.role)
  const label = [
    named.filter(swatch => swatch.hex !== null).map(swatch => swatch.label).join(', '),
    missing.length === 0 ? '' : `${inWords(missing)} ${missing.length === 1 ? 'is' : 'are'} not set`,
  ].filter(Boolean).join('. ')

  return (
    <svg width="86" height="14" viewBox="0 0 86 14" role="img" aria-label={`${owner ? `${owner}'s email colors` : 'Email colors'}: ${label}`} className="block flex-none">
      {named.map((swatch, index) => (
        <g key={swatch.role} data-role={swatch.role}>
          {swatch.hex !== null && <rect x={index * 18} y="0" width="14" height="14" rx="4" fill={swatch.hex} />}
          <rect x={index * 18 + 0.5} y="0.5" width="13" height="13" rx="3.5" fill="none" strokeWidth={1}
            strokeDasharray={swatch.hex === null ? '2 2' : undefined} className="stroke-slate-400" />
        </g>
      ))}
    </svg>
  )
}
